/**
 * PrivacyPolicy — public, no-login page for A2P 10DLC verification (Jose 2026-08-22).
 * Carrier reviewers must be able to reach a compliant privacy policy that covers the SMS
 * program (consent, frequency, STOP/HELP, and an explicit "we don't sell/share numbers"
 * clause). Served at https://slik-pos-public.web.app/privacy.
 */
import React from 'react';

const PrivacyPolicy: React.FC = () => (
  <div className="min-h-screen bg-gray-50 py-10 px-4">
    <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-card p-6 md:p-8 text-gray-800 leading-relaxed">
      <a href="/" className="text-slik-purple text-sm">&larr; Back to SLIK Barber Lounge</a>
      <h1 className="text-2xl font-bold text-slik-navy mt-4 mb-1">Privacy Policy</h1>
      <p className="text-xs text-gray-400 mb-6">SLIK Barber Lounge &middot; Last updated August 2026</p>

      <p className="mb-4">
        SLIK Barber Lounge (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;) respects your privacy.
        This policy explains what information we collect and how we use it, including our SMS/text
        messaging program.
      </p>

      <h2 className="text-lg font-semibold text-slik-navy mt-6 mb-2">Information we collect</h2>
      <p className="mb-4">
        When you book an appointment &mdash; in person at the shop, by phone, or through this website
        &mdash; we collect your name, mobile phone number, and email so we can schedule and service your
        appointment and keep your account up to date.
      </p>

      <h2 className="text-lg font-semibold text-slik-navy mt-6 mb-2">SMS / text messaging</h2>
      <p className="mb-2">
        By providing your mobile number and booking an appointment, you agree to receive account and
        appointment text messages from SLIK Barber Lounge, such as booking confirmations, reminders,
        receipts, rescheduling and waitlist notifications, and &ldquo;on my way&rdquo; updates.
      </p>
      <ul className="list-disc pl-5 mb-4 space-y-1">
        <li>Message frequency varies based on your appointments and activity.</li>
        <li>Message and data rates may apply.</li>
        <li>Reply <b>STOP</b> to any message to unsubscribe at any time. Reply <b>HELP</b> for help.</li>
        <li>Consenting to receive texts is <b>not</b> a condition of any purchase.</li>
      </ul>

      <h2 className="text-lg font-semibold text-slik-navy mt-6 mb-2">We never sell or share your data</h2>
      <p className="mb-4">
        <b>
          We do not sell, rent, or share your phone number or SMS opt-in information with any third
          parties or affiliates for their own marketing purposes.
        </b>{' '}
        Your information is used only to operate SLIK Barber Lounge&rsquo;s services, and is shared solely
        with the vendors that help us deliver those services (for example, our SMS provider and payment
        processor), who are contractually required to protect it.
      </p>

      <h2 className="text-lg font-semibold text-slik-navy mt-6 mb-2">Contact us</h2>
      <p className="mb-1">
        Questions about this policy or your information? Reach SLIK Barber Lounge through our website at{' '}
        <a href="https://slikbarberlounge.com" className="text-slik-purple underline">slikbarberlounge.com</a>,
        or reply to any text message you receive from us.
      </p>
    </div>
  </div>
);

export default PrivacyPolicy;
